import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isAdmin, useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { useTutorStudents } from '@/lib/tutor/queries';
import { useMyPeers } from '@/lib/student/peers';
import { useFollowedTeachers } from '@/lib/student/teacher-follows';
import type { ViewerRole } from '@/components/news/types';
import { useNewsFeed, type UseNewsFeedOptions, type UseNewsFeedResult } from './use-news-feed';
import { countUnreadFeedItems } from './feed-unread';
import { useFeedSeenAt } from './feed-seen';

function uniq(ids: string[]): string[] {
  return Array.from(new Set(ids));
}

/**
 * Feed de l'utilisateur courant pour SON rôle — source unique partagée par la
 * page Feed et le badge de navigation (mêmes clés React Query : aucune requête
 * en double). Les hooks spécifiques aux autres rôles sont désactivés (id vide).
 */
export function useRoleNewsFeed(
  role: ViewerRole,
  opts: {
    /** Admin seulement : activités de TOUS les comptes (migration 0347). */
    allUsers?: boolean;
  } = {},
): UseNewsFeedResult {
  const { user, profile } = useAuth();
  const userId = user?.id ?? '';
  const admin = isAdmin(profile, user?.email);
  const allUsers = admin && opts.allUsers === true;

  // ── Prof : élèves liés actifs ──────────────────────────────────────────────
  const tutorStudents = useTutorStudents(role === 'tutor' ? userId : '');
  const activeStudentIds = useMemo(
    () => (tutorStudents.data ?? []).filter((s) => s.status === 'active').map((s) => s.student_id),
    [tutorStudents.data],
  );

  // ── Parent : enfants liés actifs ──────────────────────────────────────────
  const parentChildren = useQuery({
    queryKey: ['parent-active-children', userId],
    enabled: role === 'parent' && !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parent_links')
        .select('student_id')
        .eq('parent_id', userId)
        .eq('status', 'active');
      if (error) throw error;
      return (data ?? []).map((l) => l.student_id);
    },
  });

  // ── Élève : camarades + profs suivis ──────────────────────────────────────
  const peersQuery = useMyPeers(role === 'student' ? userId : '');
  const teachersQuery = useFollowedTeachers(role === 'student' ? userId : '');
  const peerIds = useMemo(() => (peersQuery.data ?? []).map((p) => p.id), [peersQuery.data]);
  const teacherIds = useMemo(
    () => (teachersQuery.data ?? []).map((t) => t.id),
    [teachersQuery.data],
  );

  const scopeLoading =
    (role === 'tutor' && tutorStudents.isLoading) ||
    (role === 'parent' && parentChildren.isLoading);

  const options = useMemo<UseNewsFeedOptions>(() => {
    if (allUsers) {
      return {
        scopeKey: `admin-all:${userId}`,
        assignmentScope: { type: 'all' },
        challengeUserIds: [],
        attemptStudentIds: [],
        submissionScope: { type: 'all' },
        includeVisio: true,
        includeQuestionReports: true,
        includeDailyRecaps: true,
        includeStartedAttempts: true,
        allUsers: true,
        viewerRole: 'tutor',
        enabled: !!userId,
      };
    }
    if (role === 'tutor') {
      return {
        scopeKey: `tutor:${userId}`,
        assignmentScope: { type: 'tutor', tutorId: userId },
        challengeUserIds: userId ? [userId, ...activeStudentIds] : [],
        attemptStudentIds: activeStudentIds,
        submissionScope: { type: 'tutor', tutorId: userId },
        includeVisio: true,
        evaluationStudentIds: activeStudentIds,
        // L'admin lit toutes les salles (RLS) : on garde les siennes ici.
        visioOwnerId: admin ? userId : undefined,
        includeQuestionReports: true,
        // Admin : récap journalier de l'activité (0523).
        includeDailyRecaps: admin,
        // Admin : il voit aussi les exercices commencés de ses élèves (0504).
        includeStartedAttempts: admin,
        viewerRole: 'tutor',
        enabled: !!userId && !scopeLoading,
      };
    }
    if (role === 'parent') {
      const studentIds = parentChildren.data ?? [];
      return {
        scopeKey: `parent:${userId}`,
        assignmentScope: { type: 'parent', studentIds },
        challengeUserIds: studentIds,
        attemptStudentIds: studentIds,
        diagnosticStudentIds: studentIds,
        viewerRole: 'parent',
        enabled: !!userId && !scopeLoading,
      };
    }
    if (role === 'player') {
      return {
        scopeKey: `player:${userId}`,
        assignmentScope: { type: 'student', studentId: userId },
        challengeUserIds: userId ? [userId] : [],
        attemptStudentIds: userId ? [userId] : [],
        viewerRole: 'player',
        enabled: !!userId,
      };
    }
    const sortedPeers = [...peerIds].sort();
    const sortedTeachers = [...teacherIds].sort();
    return {
      // Clé de cache dérivée du contexte pour éviter les croisements entre élèves.
      scopeKey: `student:${userId}|peers:${sortedPeers.join(',')}|teachers:${sortedTeachers.join(',')}`,
      assignmentScope: { type: 'student', studentId: userId },
      // Les profs comme les camarades peuvent publier des défis…
      challengeUserIds: userId ? uniq([userId, ...peerIds, ...teacherIds]) : [],
      // …mais seuls les élèves font des essais.
      attemptStudentIds: userId ? uniq([userId, ...peerIds]) : [],
      submissionScope: { type: 'student', studentId: userId },
      skillcardStudentId: userId,
      evaluationStudentIds: userId ? [userId] : [],
      includeVisio: true,
      viewerRole: 'student',
      enabled: !!userId,
    };
  }, [
    allUsers,
    admin,
    role,
    userId,
    activeStudentIds,
    parentChildren.data,
    peerIds,
    teacherIds,
    scopeLoading,
  ]);

  const feed = useNewsFeed(options);
  return { ...feed, isLoading: feed.isLoading || scopeLoading };
}

/**
 * Nombre de nouveautés du Feed depuis sa dernière ouverture (badge de nav).
 * 0 tant que la date de dernière ouverture n'est pas connue.
 */
export function useFeedUnreadCount(role: ViewerRole): number {
  const { user } = useAuth();
  const { items } = useRoleNewsFeed(role);
  const seen = useFeedSeenAt();
  return useMemo(() => {
    if (!seen.isSuccess) return 0;
    return countUnreadFeedItems(items, seen.data, user?.id);
  }, [items, seen.isSuccess, seen.data, user?.id]);
}
